import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import * as XLSX from 'xlsx';
import {reportFieldLabels} from '../../../types/report.consts';
import {ReportData} from '../../../types/report.types';
import {SubUser} from '../../../services/models/sub_user';
import {ReportAPI} from '../../../services/network/report-api';
import {useFetchAllReportsForExcel} from '../../../hooks/useQuery/useFetchAllReportsForExcel';
import stylesButtons from "../../../assets/styles/buttons.module.css";

interface ExportToExcelProps {
    reportData: ReportData;
    isAdmin: boolean;
    selectedWeek: number;
    selectedYear: number;
    userName: string;
    logsData?: any[];
    users?: SubUser[]; // Добавляем пользователей для администратора
}

// Типы для форматирования данных Excel
interface ExcelReportData {
    'Показатель': string;
    'Текущее значение'?: number | string;
    'Итого за год'?: number | string;
    'Итого с прошлым годом'?: number | string;
}

interface ExcelLogData {
    'ФИО пользователя': string;
    'Дата': string;
    'Неделя отчетности': number;
    'Год отчетности': number;
    'Показатель': string;
    'Старое значение': number;
    'Новое значение': number;
}

export const ExportToExcel: React.FC<ExportToExcelProps> = ({
                                                                reportData,
                                                                isAdmin,
                                                                selectedWeek,
                                                                selectedYear,
                                                                userName,
                                                                logsData,
                                                                users = []
                                                            }) => {
    const [isLoading, setIsLoading] = useState(false);

    // Используем хук для получения всех отчетов для Excel
    // Хук будет активирован только при клике на кнопку экспорта
    const [shouldFetchAllReports, setShouldFetchAllReports] = useState(false);
    const {reportsData, reportsError, reportsIsLoading} = useFetchAllReportsForExcel(
        shouldFetchAllReports ? selectedWeek : undefined,
        shouldFetchAllReports ? selectedYear : undefined,
    );

    //('Состояние запроса:', { shouldFetchAllReports, reportsIsLoading, reportsData, reportsError });

    // Состояние для хранения данных всех пользователей
    const [allUsersData, setAllUsersData] = useState<Record<string, ReportData>>({});

    // Функция для обработки данных, полученных через хук или API
    const processReportsData = (data?: any) => {
        //('Получены данные для обработки:', data);

        // Используем данные из аргумента или из хука
        const reportsToProcess = data || reportsData;

        if (!reportsToProcess) {
            toast.error('Отсутствуют данные отчетов');
            return;
        }

        // Создаем объект для хранения данных всех пользователей
        const userData: Record<string, ReportData> = {};

        // Проверяем формат данных
        if (typeof reportsToProcess === 'object' && reportsToProcess !== null && !Array.isArray(reportsToProcess)) {
            // Новый формат - объект с ключами summary и ID пользователей
            //('Обрабатываем данные в новом формате:', Object.keys(reportsToProcess).length, 'ключей');
            //('Ключи в данных:', Object.keys(reportsToProcess));

            // Обрабатываем каждый ключ в объекте
            for (const [key, value] of Object.entries(reportsToProcess)) {
                //('Обрабатываем ключ:', key, 'значение:', value);

                if (key === 'summary') {
                    // Добавляем сводные данные с ключом 'summary'
                    userData['summary'] = value as ReportData;
                    //('Добавлены сводные данные');
                } else {
                    // Добавляем данные пользователя с ключом ID
                    // Находим пользователя по ID
                    const user = users.find(u => u.id === key);
                    const userKey = user ? (user.email || user.id) : key;

                    userData[userKey] = value as ReportData;
                    //('Добавлены данные пользователя:', userKey);
                }
            }
        } else {
            toast.error('Неподдерживаемый формат данных:', reportsToProcess);
            return;
        }

        // Записываем обработанные данные в состояние
        setAllUsersData(userData);

        // Только для администратора запускаем экспорт после получения данных из хука
        if (isAdmin) {
            exportToExcel();
        }
    };

    // Эффект для обработки данных, полученных через хук
    useEffect(() => {
        if (shouldFetchAllReports && !reportsIsLoading && reportsData) {
            //('Данные получены через хук:', reportsData);
            processReportsData();
        }
    }, [reportsData, reportsIsLoading, shouldFetchAllReports]);

    // Проверяем наличие данных для экспорта
    if (!reportData || (!reportData.resultPO && !reportData.resultPOChildren)) {
        return null;
    }

    // Функция для форматирования данных отчета в формат для Excel
    const formatReportDataForExcel = (customUserName?: string, userData?: ReportData): ExcelReportData[] => {
        // Если данные пользователя не переданы, используем данные текущего пользователя
        const data = userData || reportData;
        //('Форматируем данные для', customUserName || userName, 'данные:', data);

        const result: ExcelReportData[] = [];

        // Добавляем заголовок отчета - только название без значений
        result.push({
            'Показатель': `Наименование субъекта`,
            'Текущее значение': customUserName === 'Сводная таблица' ? "ДНР+ЛНР+ХО+ЗО" : customUserName
        });


        // Пустая строка для разделения
        result.push({
            'Показатель': '',
            'Текущее значение': '',
            'Итого за год': '',
            'Итого с прошлым годом': ''
        });

        // Добавляем данные из resultPO в порядке, определенном в report.consts.ts
        // Сначала заголовок раздела
        result.push({
            'Показатель': 'Результаты профилактических осмотров в 2025 году',
            'Текущее значение': 'За отчетную неделю',
            'Итого за год': 'нарастающим итогом, с начала года',
            'Итого с прошлым годом': ''
        });

        // Перебираем поля в порядке, определенном в report.consts.ts
        const resultPOFields = Object.keys(reportFieldLabels.resultPO) as Array<keyof typeof reportFieldLabels.resultPO>;

        for (const key of resultPOFields) {
            const values = data.resultPO?.[key];
            // Для resultPO используем массив [weekly, total]
            const weekly = Array.isArray(values) && values.length > 0 ? values[0] : 0;
            const total = Array.isArray(values) && values.length > 1 ? values[1] : 0;

            result.push({
                'Показатель': reportFieldLabels.resultPO[key],
                'Текущее значение': weekly,
                'Итого за год': total,
                'Итого с прошлым годом': ''
            });
        }

        // Пустая строка для разделения
        result.push({
            'Показатель': '',
            'Текущее значение': '',
            'Итого за год': '',
            'Итого с прошлым годом': ''
        });

        // Добавляем данные из resultPOChildren в порядке, определенном в report.consts.ts
        // Сначала заголовок раздела
        result.push({
            'Показатель': 'Результаты ПО детей',
            'Текущее значение': 'За отчетную неделю',
            'Итого за год': 'нарастающим итогом, с начала года',
            'Итого с прошлым годом': 'Суммарно с данными прошлого года'
        });

        // Перебираем поля в порядке, определенном в report.consts.ts
        const resultPOChildrenFields = Object.keys(reportFieldLabels.resultPOChildren) as Array<keyof typeof reportFieldLabels.resultPOChildren>;

        for (const key of resultPOChildrenFields) {
            const values = data.resultPOChildren?.[key];
            // Для resultPOChildren используем массив [weekly, total, totalWithPrevYear]
            const weekly = Array.isArray(values) && values.length > 0 ? values[0] : 0;
            const total = Array.isArray(values) && values.length > 1 ? values[1] : 0;
            const totalWithPrevYear = Array.isArray(values) && values.length > 2 ? values[2] : total;

            result.push({
                'Показатель': reportFieldLabels.resultPOChildren[key],
                'Текущее значение': weekly,
                'Итого за год': total,
                'Итого с прошлым годом': totalWithPrevYear
            });
        }

        return result;
    };


    // Функция для применения стилей к Excel-листу
    // ВНИМАНИЕ: Для работы со стилями (рамки, цвет и т.д.) требуется SheetJS Pro или xlsx-style!
    function applyStyles(ws: XLSX.WorkSheet) {
        if (!ws['!ref']) return;
        const range = XLSX.utils.decode_range(ws['!ref']);
        // Устанавливаем ширину столбцов
        ws['!cols'] = [
            {wch: 50}, // Ширина первого столбца (Показатель)
            {wch: 20}, // Ширина второго столбца (Текущее значение)
            {wch: 40}, // Ширина третьего столбца (Итого за год)
            {wch: 25}  // Ширина четвертого столбца (Итого с прошлым годом)
        ];
        // Устанавливаем высоту строк
        ws['!rows'] = [];
        for (let i = 0; i <= range.e.r; i++) {
            ws['!rows'][i] = {hpt: 25};
        }
        // Обводим рамкой все непустые ячейки (SheetJS Pro/xlsx-style)
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = XLSX.utils.encode_cell({r: R, c: C});
                const cell = ws[cell_address];
                if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
                    cell.s = cell.s || {};
                    cell.s.border = {
                        top: {style: "thin"},
                        bottom: {style: "thin"},
                        left: {style: "thin"},
                        right: {style: "thin"}
                    };
                }
            }
        }
    }

    // Функция для форматирования данных логов в формат для Excel
    const formatLogsDataForExcel = (logs: any[]): ExcelLogData[] => {
        return logs.map(item => ({
            'ФИО пользователя': item.userFullName || '',
            'Дата': item.formattedDate || '',
            'Неделя отчетности': item.reportingWeek || 0,
            'Год отчетности': item.reportingYear || 0,
            'Показатель': item.fieldName || '',
            'Старое значение': item.oldValue || 0,
            'Новое значение': item.newValue || 0
        }));
    };

    // Функция для обработки нажатия на кнопку экспорта
    const onExportButtonClick = () => {
        if (isLoading || reportsIsLoading) return;

        if (isAdmin) {
            // Для администратора активируем хук, чтобы получить данные всех пользователей
            //('Запускаем запрос данных для администратора');
            setIsLoading(true);

            // Вызываем API напрямую, так как хук не работает корректно
            ReportAPI.fetchAllReportsForExcel(selectedWeek, selectedYear)
                .then(response => {
                    //('Получен ответ от API:', response);
                    if (response.status === 200 && response.data) {
                        // Проверяем формат данных
                        const data = response.data;
                        //('Структура полученных данных:', Object.keys(data));

                        // Создаем объект для хранения данных всех пользователей
                        const userData: Record<string, ReportData> = {};

                        // Обрабатываем каждый ключ в объекте
                        for (const [key, value] of Object.entries(data)) {
                            if (key === 'summary') {
                                // Добавляем сводные данные с ключом 'summary'
                                userData['summary'] = value as unknown as ReportData;
                                //('Добавлены сводные данные');
                            } else {
                                // Добавляем данные пользователя с ключом ID
                                // Находим пользователя по ID
                                const user = users.find(u => u.id === key);
                                const userKey = user ? (user.email || user.id) : key;

                                userData[userKey] = value as unknown as ReportData;
                            }
                        }

                        // Записываем обработанные данные в состояние
                        //('Обработано пользователей:', Object.keys(userData).length);

                        // Запускаем экспорт в Excel напрямую с данными
                        exportToExcel(userData);
                    } else {
                        toast.error('Ошибка при получении данных:', response);
                        setIsLoading(false);
                    }
                })
                .catch(error => {
                    toast.error('Ошибка при запросе данных:', error);
                    setIsLoading(false);
                });
        } else {
            // Для обычного пользователя сразу запускаем экспорт
            //('Запускаем экспорт для обычного пользователя');
            exportToExcel();
        }
    };

    // Универсальная функция для экспорта данных в Excel
    const exportToExcel = async (userData?: Record<string, ReportData>) => {
        if (isLoading) return;
        try {
            setIsLoading(true);
            const wb: XLSX.WorkBook = XLSX.utils.book_new();
            if (isAdmin) {

                const dataToExport = userData || allUsersData;
                if (!dataToExport || Object.keys(dataToExport).length === 0) {
                    toast.error('Нет данных для экспорта');
                    setIsLoading(false);
                    return;
                }
                // Сводная таблица
                if (dataToExport['summary']) {
                    const summaryExcelData = formatReportDataForExcel('Сводная таблица', dataToExport['summary']);
                    const summarySheet = XLSX.utils.json_to_sheet(summaryExcelData, {
                        header: ["Показатель", "Текущее значение", "Итого за год", "Итого с прошлым годом"],
                        skipHeader: true
                    });
                    applyStyles(summarySheet);
                    XLSX.utils.book_append_sheet(wb, summarySheet, `Сводная таблица`);
                }
                // Листы пользователей
                Object.keys(dataToExport).forEach(userKey => {

                    if (userKey === 'summary') return;
                    const userData = dataToExport[userKey];
                    const userDisplayName = userKey === userName ? userName : userData.ownerSectorName;
                    const userExcelData = formatReportDataForExcel(userDisplayName, userData);
                    const userSheet = XLSX.utils.json_to_sheet(userExcelData, {
                        header: ["Показатель", "Текущее значение", "Итого за год", "Итого с прошлым годом"],
                        skipHeader: true
                    });
                    applyStyles(userSheet);
                    let sheetName = userData.ownerSectorName;
                    if (userKey === userName) {
                        sheetName = userData.ownerSectorName || userName;
                    } else {
                        const user = users.find(u => u.email === userKey || u.id === userKey);
                        if (user) {
                            sheetName = user.full_name || user.email || userKey;
                        }
                    }
                    XLSX.utils.book_append_sheet(wb, userSheet, sheetName);
                });
                // Логи
                if (logsData && logsData.length > 0) {
                    const logsDataForExcel = formatLogsDataForExcel(logsData);
                    const logsSheet = XLSX.utils.json_to_sheet(logsDataForExcel);
                    XLSX.utils.book_append_sheet(wb, logsSheet, 'Логи изменений');
                }
            } else {
                const formattedData = formatReportDataForExcel();
                const ws = XLSX.utils.json_to_sheet(formattedData, {
                    header: ["Показатель", "Текущее значение", "Итого за год", "Итого с прошлым годом"],
                    skipHeader: true
                });
                applyStyles(ws);
                XLSX.utils.book_append_sheet(wb, ws, userName);
                if (logsData && logsData.length > 0) {
                    const formattedLogs = formatLogsDataForExcel(logsData);
                    const logsWs = XLSX.utils.json_to_sheet(formattedLogs);
                    XLSX.utils.book_append_sheet(wb, logsWs, 'Логи изменений');
                }
            }
            const date = new Date();
            const fileName = `Отчет_Неделя${selectedWeek}_${date.toLocaleDateString().replace(/\./g, '-')}.xlsx`;
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            toast.error('Ошибка при экспорте в Excel');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            onClick={onExportButtonClick}
            className={`${stylesButtons.iconButton} ${stylesButtons.exportButton} w-100`}
            disabled={isLoading || reportsIsLoading}
        >
            Экспорт&nbsp;в&nbsp;Excel
        </button>
    );
};